import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { adminResolveMatch } from '../api';
import { toast } from '../toast';

const KEY_STORAGE = 'wcfantasy.admin-key';

interface Props {
  matchId: string;
  homeName: string;
  awayName: string;
  homeScore: number | null;
  awayScore: number | null;
}

export default function AdminResolveMatch({ matchId, homeName, awayName, homeScore, awayScore }: Props) {
  const qc = useQueryClient();
  const [winner, setWinner] = useState<'home' | 'away' | ''>('');
  const [phase, setPhase] = useState<'ot' | 'pk'>('pk');
  const [homePk, setHomePk] = useState<string>('');
  const [awayPk, setAwayPk] = useState<string>('');
  const [busy, setBusy] = useState(false);

  let adminKey = '';
  try { adminKey = localStorage.getItem(KEY_STORAGE) ?? ''; } catch { /* ignore */ }
  if (!adminKey) return null;

  async function submit() {
    if (!winner) { toast.error('Pick the winning side.'); return; }
    setBusy(true);
    try {
      await adminResolveMatch(matchId, adminKey, {
        winner,
        ended_phase: phase,
        home_pk_score: phase === 'pk' && homePk !== '' ? Number(homePk) : null,
        away_pk_score: phase === 'pk' && awayPk !== '' ? Number(awayPk) : null,
      });
      toast.success('Match resolved.');
      qc.invalidateQueries({ queryKey: ['match', matchId] });
      qc.invalidateQueries({ queryKey: ['leaderboard'] });
      qc.invalidateQueries({ queryKey: ['schedule'] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-4 ring-1 ring-amber-500/30">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-amber-400">Admin · Resolve stuck knockout</h2>
        <span className="text-[10px] text-dim">ESPN didn't supply an ET/PK winner</span>
      </div>
      <p className="text-[11px] text-dim mb-3">
        Regulation ended {homeScore ?? '?'}-{awayScore ?? '?'}. Pick who advanced and how.
        Marks the match as manually resolved so the cron won't overwrite it on the next ESPN poll.
      </p>
      <div className="space-y-2.5">
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button" onClick={() => setWinner('home')}
            className={`py-1.5 text-xs rounded-md ${winner === 'home' ? 'bg-pitch-500/20 text-pitch-400 ring-1 ring-pitch-500/40' : 'bg-ink-800 text-chalk'}`}
          >{homeName} advanced</button>
          <button
            type="button" onClick={() => setWinner('away')}
            className={`py-1.5 text-xs rounded-md ${winner === 'away' ? 'bg-pitch-500/20 text-pitch-400 ring-1 ring-pitch-500/40' : 'bg-ink-800 text-chalk'}`}
          >{awayName} advanced</button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button" onClick={() => setPhase('ot')}
            className={`py-1.5 text-xs rounded-md ${phase === 'ot' ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/40' : 'bg-ink-800 text-chalk'}`}
          >Won in extra time</button>
          <button
            type="button" onClick={() => setPhase('pk')}
            className={`py-1.5 text-xs rounded-md ${phase === 'pk' ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/40' : 'bg-ink-800 text-chalk'}`}
          >Won on penalties</button>
        </div>
        {phase === 'pk' && (
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <input
              type="number" inputMode="numeric" min={0} max={20}
              value={homePk} onChange={(e) => setHomePk(e.target.value)}
              placeholder={`${homeName} PK`}
              className="bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk font-mono"
            />
            <span className="text-dim text-sm">–</span>
            <input
              type="number" inputMode="numeric" min={0} max={20}
              value={awayPk} onChange={(e) => setAwayPk(e.target.value)}
              placeholder={`${awayName} PK`}
              className="bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk font-mono"
            />
          </div>
        )}
        <button
          type="button" onClick={submit} disabled={busy || !winner}
          className="w-full h-10 rounded-lg bg-amber-500 text-ink-950 font-semibold text-sm disabled:opacity-50"
        >{busy ? 'Resolving…' : 'Set result'}</button>
      </div>
    </section>
  );
}
